import { useState } from 'react';
import { useLeasingUrlParams } from '../../../../hooks/useLeasingUrlParams';

import {
  Box,
  Button,
  ButtonGroup,
  Card,
  FormField,
  FormHelp,
  Heading,
  Icon,
  IconButton,
  Image,
  Input,
  Label,
  Paragraph,
  TextButton,
} from '@zillow/constellation-10';
import { IconDeleteFilled, IconRenameFilled } from '@zillow/constellation-icons';
import { FULLSTORY_EXCLUDE } from '../../../../constants/analytics';
import type { DocumentDetailsDto } from '@zg-rentals/ts-leasing-client';
import {
  useDeleteLeaseDocumentMutation,
  useRenameLeaseDocumentsMutation,
  useUploadedFileDetailsQuery,
} from '../../../../utils/queries';
import LeaseModal from '../../../Containers/LeaseModal';
import { CANCEL } from '../../../../constants/stringConstants';

type Props = {
  leaseDetail: DocumentDetailsDto;
};

function DocumentCard({ leaseDetail }: Props) {
  const { leaseId } = useLeasingUrlParams();
  const { name } = leaseDetail;
  const [documentName, setDocumentName] = useState(name);
  const [isRenaming, setIsRenaming] = useState(false);

  const deleteMutation = useDeleteLeaseDocumentMutation();
  const renameMutation = useRenameLeaseDocumentsMutation();
  const uploadedFilesQuery = useUploadedFileDetailsQuery(leaseId);

  const isMutating = deleteMutation.isPending || renameMutation.isPending || uploadedFilesQuery.isFetching;

  function onRenameClick() {
    setIsRenaming(true);
  }

  function onRenameClose() {
    setIsRenaming(false);
  }

  async function onRenameSave() {
    await renameMutation.mutateAsync({
      postData: {
        leaseId: leaseId as unknown as number,
        renames: [{ documentId: leaseDetail.id, name: documentName }],
      },
    });
    setIsRenaming(false);
  }

  async function onDeleteClick() {
    await deleteMutation.mutateAsync({
      postData: {
        leaseId: leaseId as unknown as number,
        documentId: leaseDetail.id,
      },
    });
  }

  return (
    <>
      <LeaseModal
        id="rename-document"
        onClose={onRenameClose}
        isOpen={isRenaming}
        header={<Heading level={2}>Rename document</Heading>}
        body={
          <FormField
            className={FULLSTORY_EXCLUDE}
            required
            label={<Label>Name</Label>}
            control={<Input value={documentName} onChange={(e) => setDocumentName(e.target.value)} />}
            description={<FormHelp>Your tenant will see this file name when signing the document</FormHelp>}
          />
        }
        footer={
          <ButtonGroup aria-label="rename document">
            <TextButton onClick={onRenameClose} disabled={isMutating}>
              {CANCEL}
            </TextButton>
            <Button
              tone="brand"
              emphasis="filled"
              onClick={onRenameSave}
              disabled={!documentName.trim() || isMutating}
              loading={isMutating}
            >
              Save
            </Button>
          </ButtonGroup>
        }
      />
      <Card tone="neutral" elevated={false}>
        <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: 'md',
              minWidth: '4rem',
              maxWidth: '4rem',
            }}
          >
            <Image
              alt="Document preview"
              src={leaseDetail.imageUrl}
              css={{ objectFit: 'contain', maxHeight: '3rem', maxWidth: '4rem', width: '100%' }}
            />
          </Box>
          <Box css={{ flex: 1, marginRight: 'md' }}>
            <Paragraph css={{ wordBreak: 'break-all' }}>{leaseDetail.name}</Paragraph>
          </Box>
          <ButtonGroup aria-label="document options">
            <IconButton
              title="Rename"
              emphasis="bare"
              tone="brand"
              shape="square"
              onClick={onRenameClick}
              disabled={isMutating}
            >
              <Icon>
                <IconRenameFilled />
              </Icon>
            </IconButton>
            <IconButton
              title="Delete"
              emphasis="bare"
              tone="brand"
              shape="square"
              onClick={onDeleteClick}
              disabled={isMutating}
            >
              <Icon>
                <IconDeleteFilled />
              </Icon>
            </IconButton>
          </ButtonGroup>
        </Box>
      </Card>
    </>
  );
}

export default DocumentCard;
